import { useState } from "react";
import { staffApi, ApiError } from "@/api";
import type { Quote } from "@/types";
import { computeTotals, formatMoney, defaultSelectedOptionalIds } from "@/lib/money";
import { Button, TextField } from "@/components/ui";

/**
 * Accept-on-behalf confirmation dialog. For acceptances that arrive outside the
 * quote page (typically an email reply): staff pick the optional items the
 * client asked for, confirm who accepted, and the server runs the same
 * acceptance as the public page — snapshotting accepted_total and locking the
 * quote — with an audit line recording the acting staff user.
 *
 * The total shown here is display-only; the server recomputes from DB
 * line_totals exactly as the public accept does.
 */
export function AcceptOnBehalfModal({
  quote,
  onAccepted,
  onClose,
}: {
  quote: Quote;
  onAccepted: () => void;
  onClose: () => void;
}) {
  const lineItems = quote.line_items ?? [];
  const optionalItems = lineItems
    .filter((li) => li.is_optional)
    .sort((a, b) => a.position - b.position);

  const [name, setName] = useState(
    [quote.contact_first_name, quote.contact_last_name].filter(Boolean).join(" "),
  );
  // Seed the ticks from default_selected — the same initial state the client
  // saw on the public page; staff adjust to match what the email agreed to.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(defaultSelectedOptionalIds(lineItems)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const totals = computeTotals(lineItems, quote.vat_treatment || "inclusive", selectedIds);

  function toggle(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function confirm() {
    if (!name.trim()) {
      setError("Enter the name of the person who accepted.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await staffApi.acceptQuoteOnBehalf(quote.id, name.trim(), [...selectedIds]);
      onAccepted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not accept the quote.");
      setBusy(false);
    }
  }

  return (
    <div className="aob__overlay" onClick={onClose}>
      <div className="aob" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Accept on behalf of the client">
        <header className="aob__head">
          <h3>Accept on behalf of the client</h3>
          <button type="button" className="aob__close" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="aob__body">
          <p className="aob__intro">
            Records an acceptance received outside the quote page — e.g. an email reply.
            This locks the quote exactly as if the client had accepted online, and notes
            in the quote that you entered it. It can't be undone from the app.
          </p>

          <TextField
            label="Accepted by (the client's name)"
            required
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />

          {optionalItems.length > 0 ? (
            <fieldset className="aob__options">
              <legend>Optional items the client agreed to</legend>
              {optionalItems.map((li) => (
                <label key={li.id} className="aob__option">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(li.id)}
                    disabled={busy}
                    onChange={() => toggle(li.id)}
                  />
                  <span className="aob__option-title">{li.title}</span>
                  <span className="aob__option-price numeric">
                    {formatMoney(li.line_total ?? 0, quote.currency)}
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}

          <p className="aob__total">
            Accepted total: <strong className="numeric">{formatMoney(totals.total, quote.currency)}</strong>
          </p>

          {error ? <p className="quotes-list__error" role="alert">{error}</p> : null}
        </div>

        <footer className="aob__foot">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={confirm} loading={busy}>
            Accept quote
          </Button>
        </footer>
      </div>
    </div>
  );
}
